import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Box, Stack, Typography, useTheme } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { SvgIconComponent } from '@mui/icons-material';
import ChatBubbleOutlineRounded from '@mui/icons-material/ChatBubbleOutlineRounded';
import PlayCircleOutlineRounded from '@mui/icons-material/PlayCircleOutlineRounded';
import BookOutlined from '@mui/icons-material/BookOutlined';
import CampaignOutlined from '@mui/icons-material/CampaignOutlined';
import { show } from '@intercom/messenger-js-sdk';
import useUser from '../../../hooks/global/useUser';
import useMeetWithSpecialist from '../../../hooks/businesses/useMeetWithSpecialist';
import { LoadedUser } from '../../../interfaces';
import IFrameModal from '../../modal/IFrameModal';
import {
  PRODUCT_UPDATES_URL,
  ROUTES,
  SUPPORT_URL,
} from '../../../constants/global';
import { openExternalUrl } from '../../../utils/global/helper';
import { UNSTYLED_BUTTON_SX } from '../../../constants/ui';
import { LEARN_IS_HIDDEN } from '../../../constants/learn';

interface Resource {
  label: string;
  icon: SvgIconComponent;
  /** An in-app destination. Anything else does its work in onClick. */
  to?: string;
  onClick?: () => void;
}

const RESOURCES: Resource[] = [
  { label: 'Chat with support', icon: ChatBubbleOutlineRounded, onClick: show },
  ...(LEARN_IS_HIDDEN
    ? []
    : [
        {
          label: 'Watch video how-tos',
          icon: PlayCircleOutlineRounded,
          to: ROUTES.learn,
        },
      ]),
  {
    label: 'Help docs',
    icon: BookOutlined,
    onClick: () => openExternalUrl(SUPPORT_URL),
  },
  {
    label: 'What’s new in Porter',
    icon: CampaignOutlined,
    onClick: () => openExternalUrl(PRODUCT_UPDATES_URL),
  },
];

function RailCard({
  children,
  emphasized = false,
}: React.PropsWithChildren<{ emphasized?: boolean }>) {
  const theme = useTheme();
  return (
    <Box
      p={2.5}
      borderRadius="10px"
      border={`1px solid ${
        emphasized ? 'black' : theme.palette.lineGrey.main
      }`}>
      {children}
    </Box>
  );
}

function SupportRail() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useUser<LoadedUser>();
  const { mutateAsync, isPending } = useMeetWithSpecialist();
  const [scheduleUrl, setScheduleUrl] = useState<string | null>(null);

  const onBookCall = async () => {
    const result = await mutateAsync({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      company: user.business.name || '',
    });
    if (!result?.url) return;
    setScheduleUrl(result.url);
  };

  const onResourceClick = ({ to, onClick }: Resource) => {
    if (to) navigate(to);
    else onClick?.();
  };

  return (
    <>
      <Stack gap={2.5} position="sticky" top="var(--sticky-top, 88px)">
        <RailCard emphasized>
          <Stack direction="row" alignItems="center" gap={1.5} mb={1.75}>
            <Avatar
              src="/onboarding-specialist.webp"
              alt=""
              sx={{ width: 44, height: 44, border: '1px solid black' }}
            />
            <Stack>
              <Typography variant="h5" component="p">
                Onboarding team
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Talk with a specialist
              </Typography>
            </Stack>
          </Stack>
          <Typography
            variant="caption"
            component="p"
            color="text.secondary"
            lineHeight={1.5}
            mb={2}>
            30 minutes on a screen share. We&apos;ll import your clients and set
            up your booking form with you.
          </Typography>
          <LoadingButton
            fullWidth
            loading={isPending}
            onClick={onBookCall}
            variant="contained"
            className="primary"
            sx={{
              // button.primary sets a 40px side padding that outranks a bare
              // sx class, and the label does not survive it at this width.
              '&.primary': {
                height: 48,
                px: 2,
                borderRadius: '50px',
                fontSize: theme.typography.pxToRem(16),
                fontWeight: 700,
                whiteSpace: 'nowrap',
              },
            }}>
            Book an onboarding call
          </LoadingButton>
        </RailCard>

        <RailCard>
          <Typography variant="h5" component="p" mb={1.5}>
            Helpful resources
          </Typography>
          <Stack gap={0.5}>
            {RESOURCES.map(resource => {
              const Icon = resource.icon;
              return (
                <Box
                  key={resource.label}
                  component="button"
                  type="button"
                  onClick={() => onResourceClick(resource)}
                  sx={{
                    ...UNSTYLED_BUTTON_SX,
                    paddingY: 1.125,
                    color: 'text.primary',
                    ':hover': { color: theme.palette.primary.main },
                  }}>
                  <Stack direction="row" alignItems="center" gap={1.25}>
                    <Icon sx={{ fontSize: 18, color: 'inherit' }} />
                    <Typography variant="body2" color="inherit">
                      {resource.label}
                    </Typography>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </RailCard>
      </Stack>

      <IFrameModal
        open={!!scheduleUrl}
        src={scheduleUrl}
        onClose={() => setScheduleUrl(null)}
        title="Schedule a meeting"
      />
    </>
  );
}

export default SupportRail;
